import { useState } from 'react';
import { X, CaretLeft, CaretRight, ArrowsOut } from '@phosphor-icons/react';

interface ImageGalleryProps {
  images: string[];
  className?: string;
}

export default function ImageGallery({ images, className = '' }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  
  if (images.length === 0) return null;
  
  const openLightbox = (index: number) => {
    setSelectedIndex(index);
  };
  
  const closeLightbox = () => {
    setSelectedIndex(null);
  };
  
  const goToPrevious = () => {
    if (selectedIndex === null) return;
    setSelectedIndex(selectedIndex === 0 ? images.length - 1 : selectedIndex - 1);
  };
  
  const goToNext = () => {
    if (selectedIndex === null) return;
    setSelectedIndex(selectedIndex === images.length - 1 ? 0 : selectedIndex + 1);
  };
  
  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goToPrevious();
    if (e.key === 'ArrowRight') goToNext();
  };

  // Single image
  if (images.length === 1) {
    return (
      <>
        <div 
          className={`relative cursor-pointer group ${className}`}
          onClick={() => openLightbox(0)}
        >
          <img 
            src={images[0]} 
            alt="" 
            className="w-full h-64 object-cover rounded-lg"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
            <ArrowsOut size={32} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        {selectedIndex !== null && (
          <Lightbox 
            images={images} 
            currentIndex={selectedIndex}
            onClose={closeLightbox}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onKeyDown={handleKeyDown}
          />
        )}
      </>
    );
  }
  
  // Two images
  if (images.length === 2) {
    return (
      <>
        <div className={`grid grid-cols-2 gap-2 ${className}`}>
          {images.map((img, idx) => (
            <div 
              key={`img-${idx}-${img.slice(-20)}`}
              className="relative cursor-pointer group"
              onClick={() => openLightbox(idx)}
            >
              <img 
                src={img} 
                alt="" 
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg" />
            </div>
          ))}
        </div>
        {selectedIndex !== null && (
          <Lightbox 
            images={images} 
            currentIndex={selectedIndex}
            onClose={closeLightbox}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onKeyDown={handleKeyDown}
          />
        )}
      </>
    );
  }
  
  // Three images
  if (images.length === 3) {
    return (
      <>
        <div className={`grid grid-cols-2 gap-2 ${className}`}>
          <div 
            className="relative cursor-pointer group row-span-2"
            onClick={() => openLightbox(0)}
          >
            <img 
              src={images[0]} 
              alt="" 
              className="w-full h-full min-h-[250px] object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg" />
          </div>
          {images.slice(1).map((img, idx) => (
            <div 
              key={`img-side-${idx}-${img.slice(-20)}`}
              className="relative cursor-pointer group"
              onClick={() => openLightbox(idx + 1)}
            >
              <img 
                src={img} 
                alt="" 
                className="w-full h-[121px] object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg" />
            </div>
          ))}
        </div>
        {selectedIndex !== null && (
          <Lightbox 
            images={images} 
            currentIndex={selectedIndex}
            onClose={closeLightbox}
            onPrevious={goToPrevious}
            onNext={goToNext}
            onKeyDown={handleKeyDown}
          />
        )}
      </>
    );
  }
  
  // Four or more images
  return (
    <>
      <div className={`grid grid-cols-2 gap-2 ${className}`}>
        {images.slice(0, 4).map((img, idx) => (
          <div 
            key={`img-grid-${idx}-${img.slice(-20)}`}
            className="relative cursor-pointer group"
            onClick={() => openLightbox(idx)}
          >
            <img 
              src={img} 
              alt="" 
              className="w-full h-32 object-cover rounded-lg"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg" />
            {idx === 3 && images.length > 4 && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <span className="text-white text-2xl font-bold">+{images.length - 4}</span>
              </div>
            )}
          </div>
        ))}
      </div>
      {selectedIndex !== null && (
        <Lightbox 
          images={images} 
          currentIndex={selectedIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
          onKeyDown={handleKeyDown}
        />
      )}
    </>
  );
}

// Lightbox component for fullscreen viewing
function Lightbox({ 
  images, 
  currentIndex, 
  onClose, 
  onPrevious, 
  onNext,
  onKeyDown 
}: { 
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}) {
  return (
    <div 
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onClick={onClose}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      {/* Close button */}
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/50 rounded-full z-10"
      >
        <X size={24} />
      </button>
      
      {/* Previous button */}
      {images.length > 1 && (
        <button 
          onClick={(e) => { e.stopPropagation(); onPrevious(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white bg-black/50 rounded-full z-10"
        >
          <CaretLeft size={32} />
        </button>
      )}
      
      {/* Image */}
      <img 
        src={images[currentIndex]} 
        alt="" 
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      {/* Next button */}
      {images.length > 1 && (
        <button 
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white bg-black/50 rounded-full z-10"
        >
          <CaretRight size={32} />
        </button>
      )}
      
      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
